/**
 * Editor de definiciones de métricas. Solo admin.
 *
 * Una definición es la "config" de una métrica (umbrales, dirección, unidad,
 * recurrencia, visibility, applies_to_projects, auto_source). Los valores
 * por período se capturan en /evaluations/kpis.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, forwardRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, ArrowLeft, Settings2 } from "lucide-react";
import {
  EVAL_LEVELS,
  EVAL_LEVEL_LABELS,
  EVAL_VISIBILITIES,
  EVAL_VISIBILITY_LABELS,
  EVAL_RECURRENCES,
  EVAL_RECURRENCE_LABELS,
  APPLIES_TO_PROJECTS_OPTS,
  APPLIES_TO_PROJECTS_LABELS,
  METRIC_DIRECTIONS,
  METRIC_DIRECTION_LABELS,
  kpiDefinitionCreateSchema,
  type KpiDefinitionCreateInput,
  type KpiDefinition,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";

export const Route = createFileRoute("/admin/kpi-definitions")({
  component: MetricDefinitionsPage,
});

function MetricDefinitionsPage() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<{ definitions: KpiDefinition[] }>({
    queryKey: ["metric-definitions", "all"],
    queryFn: () => api.get("/kpi-definitions?includeInactive=1"),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/kpi-definitions/${id}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["metric-definitions"] }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="admin"
        title="Definiciones de métricas"
        subtitle="Catálogo de métricas con sus umbrales, recurrencia y visibilidad. Los valores se capturan en Evaluaciones → Métricas."
        actions={
          <div className="flex items-center gap-2">
            <Link to="/evaluations" className="btn btn-ghost btn-sm">
              <ArrowLeft className="size-4" /> Volver
            </Link>
            <button
              type="button"
              onClick={() => dialogRef.current?.showModal()}
              className="btn btn-primary"
            >
              <Plus className="size-4" /> Nueva métrica
            </button>
          </div>
        }
      />

      {isLoading && (
        <div className="grid place-items-center py-20">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      )}

      {data && data.definitions.length === 0 ? (
        <div className="card-elevated py-16 text-center">
          <div className="size-14 rounded-full bg-primary/10 grid place-items-center mx-auto mb-4">
            <Settings2 className="size-6 text-primary" />
          </div>
          <h3 className="font-display font-semibold text-base-content mb-1">
            Sin métricas todavía
          </h3>
          <p className="text-sm text-base-content/60 mb-5">Crea la primera para empezar a medir.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {data?.definitions.map((d) => (
            <li key={d.id} className="card-elevated p-4 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                  <span className={`badge badge-sm ${levelBadgeClass(d.level)}`}>
                    {EVAL_LEVEL_LABELS[d.level]}
                  </span>
                  <span className="badge badge-ghost badge-sm">
                    {EVAL_VISIBILITY_LABELS[d.visibility]}
                  </span>
                  <span className="badge badge-ghost badge-sm">
                    {EVAL_RECURRENCE_LABELS[d.recurrence]}
                  </span>
                  {d.autoSource && (
                    <span className="badge badge-success badge-sm">auto: {d.autoSource}</span>
                  )}
                  {!d.active && <span className="badge badge-error badge-sm">inactiva</span>}
                </div>
                <h3 className="font-display font-semibold text-base-content">{d.name}</h3>
                {d.description && (
                  <p className="text-sm text-base-content/65 mt-1 line-clamp-2">{d.description}</p>
                )}
                <div className="text-xs text-base-content/55 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span>
                    Dirección:{" "}
                    <span className="font-medium">{METRIC_DIRECTION_LABELS[d.direction]}</span>
                  </span>
                  {d.unit && (
                    <span>
                      Unidad: <span className="font-mono">{d.unit}</span>
                    </span>
                  )}
                  <span>
                    Umbrales: <span className="text-success">{d.thresholdGood ?? "—"}</span>
                    {" / "}
                    <span className="text-warning">{d.thresholdImprovable ?? "—"}</span>
                    {" / "}
                    <span className="text-error">{d.thresholdBad ?? "—"}</span>
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => toggleActive.mutate({ id: d.id, active: !d.active })}
              >
                {d.active ? "Desactivar" : "Activar"}
              </button>
            </li>
          ))}
        </ul>
      )}

      <CreateDialog ref={dialogRef} onClose={() => dialogRef.current?.close()} />
    </div>
  );
}

// ─── Create dialog ────────────────────────────────────────────────────────

const CreateDialog = forwardRef<HTMLDialogElement, { onClose: () => void }>(function CreateDialog(
  { onClose },
  ref,
) {
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<KpiDefinitionCreateInput>({
    resolver: zodResolver(kpiDefinitionCreateSchema),
    defaultValues: {
      level: "organizational",
      visibility: "everyone",
      recurrence: "monthly",
      direction: "higher_better",
      appliesToProjects: "none",
      active: true,
    },
  });

  const create = useMutation({
    mutationFn: (input: KpiDefinitionCreateInput) => api.post("/kpi-definitions", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["metric-definitions"] });
      reset();
      onClose();
    },
  });

  return (
    <dialog ref={ref} className="modal" onClose={() => reset()}>
      <div className="modal-box card-elevated max-w-2xl">
        <h3 className="text-lg font-display font-semibold mb-4">Nueva métrica</h3>
        <form onSubmit={handleSubmit((d) => create.mutate(d))} className="space-y-3">
          <div>
            <label className="label py-1">
              <span className="label-text text-sm font-medium">Nombre</span>
            </label>
            <input
              className="input input-bordered w-full"
              placeholder="Ej. Llamadas canceladas %"
              {...register("name")}
            />
            {errors.name && (
              <span className="text-xs text-error mt-1 block">{errors.name.message}</span>
            )}
          </div>

          <div>
            <label className="label py-1">
              <span className="label-text text-sm font-medium">Descripción (opcional)</span>
            </label>
            <textarea
              rows={2}
              className="textarea textarea-bordered w-full"
              {...register("description")}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Nivel</span>
              </label>
              <select className="select select-bordered w-full" {...register("level")}>
                {EVAL_LEVELS.map((l) => (
                  <option key={l} value={l}>
                    {EVAL_LEVEL_LABELS[l]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Unidad</span>
              </label>
              <input
                className="input input-bordered w-full"
                placeholder="%, días, MXN…"
                {...register("unit")}
              />
            </div>
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Dirección</span>
              </label>
              <select className="select select-bordered w-full" {...register("direction")}>
                {METRIC_DIRECTIONS.map((d) => (
                  <option key={d} value={d}>
                    {METRIC_DIRECTION_LABELS[d]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Recurrencia</span>
              </label>
              <select className="select select-bordered w-full" {...register("recurrence")}>
                {EVAL_RECURRENCES.map((r) => (
                  <option key={r} value={r}>
                    {EVAL_RECURRENCE_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Visibilidad</span>
              </label>
              <select className="select select-bordered w-full" {...register("visibility")}>
                {EVAL_VISIBILITIES.map((v) => (
                  <option key={v} value={v}>
                    {EVAL_VISIBILITY_LABELS[v]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Aplica a proyectos</span>
              </label>
              <select className="select select-bordered w-full" {...register("appliesToProjects")}>
                {APPLIES_TO_PROJECTS_OPTS.map((o) => (
                  <option key={o} value={o}>
                    {APPLIES_TO_PROJECTS_LABELS[o]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 pt-2 border-t border-base-300/60">
            <div>
              <label className="label py-1">
                <span className="label-text text-xs font-medium text-success">Verde</span>
              </label>
              <input
                type="number"
                step="any"
                className="input input-bordered input-sm w-full"
                {...register("thresholdGood", {
                  setValueAs: (v) => (v === "" || v == null ? undefined : Number(v)),
                })}
              />
            </div>
            <div>
              <label className="label py-1">
                <span className="label-text text-xs font-medium text-warning">Amarillo</span>
              </label>
              <input
                type="number"
                step="any"
                className="input input-bordered input-sm w-full"
                {...register("thresholdImprovable", {
                  setValueAs: (v) => (v === "" || v == null ? undefined : Number(v)),
                })}
              />
            </div>
            <div>
              <label className="label py-1">
                <span className="label-text text-xs font-medium text-error">Rojo</span>
              </label>
              <input
                type="number"
                step="any"
                className="input input-bordered input-sm w-full"
                {...register("thresholdBad", {
                  setValueAs: (v) => (v === "" || v == null ? undefined : Number(v)),
                })}
              />
            </div>
          </div>

          <div>
            <label className="label py-1">
              <span className="label-text text-sm font-medium">
                Auto-source (opcional, identificador interno)
              </span>
            </label>
            <input
              className="input input-bordered w-full font-mono"
              placeholder="cfdi_pending, monthly_margin, …"
              {...register("autoSource")}
            />
            <p className="text-xs text-base-content/55 mt-1">
              Si lo configuras, /kpi-values/run-auto poblará el valor automáticamente.
            </p>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-3 border-t border-base-300/60">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                reset();
                onClose();
              }}
            >
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={create.isPending}>
              {create.isPending && <span className="loading loading-spinner loading-xs" />}
              Crear métrica
            </button>
          </div>
        </form>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button>cerrar</button>
      </form>
    </dialog>
  );
});

function levelBadgeClass(level: KpiDefinition["level"]): string {
  switch (level) {
    case "client_user":
      return "badge-info";
    case "organizational":
      return "badge-secondary";
    case "team":
      return "badge-accent";
    case "project":
      return "badge-primary";
  }
}
